// frontend/src/pages/usecases/JobSearch.jsx
// Use-case landing page: Job search tab management

import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  AppBar,
  Toolbar,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Extension as ExtensionIcon,
} from '@mui/icons-material';
import { useThemeMode } from '../../contexts/ThemeContext';
import { Link } from 'react-router-dom';

const CHROME_URL =
  'https://chromewebstore.google.com/detail/blackmount-%E2%80%93-save-restore/fpchhlhegjjjkfnhcdgfjcbinhhmfccc';

const STEPS = [
  {
    num: '1',
    title: 'Save your job search session',
    desc: 'One click saves every tab — listings, Glassdoor reviews, salary data, LinkedIn profiles, interview prep docs.',
  },
  {
    num: '2',
    title: 'Add notes about each company',
    desc: 'Attach quick notes so future-you remembers which role was which and what stood out.',
  },
  {
    num: '3',
    title: 'Resume on any device when you are ready to apply',
    desc: 'Open your laptop at a coffee shop, restore the session, and pick up exactly where you left off.',
  },
];

const JobSearch = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { darkMode, toggleDarkMode } = useThemeMode();

  return (
    <>
      <Helmet>
        <title>Never Lose Your Job Research — Save 30 Tabs of Listings, Prep, and Salary Data | Blackmount</title>
        <meta
          name="description"
          content="You have 30 tabs open — job listings, Glassdoor reviews, LinkedIn profiles, salary comparisons, interview prep. Save them all with one click and resume on any device."
        />
        <link rel="canonical" href="https://app.blackmount.ai/use-cases/job-search" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Never Lose Your Job Research — Save 30 Tabs | Blackmount" />
        <meta property="og:description" content="Save your entire job search session — listings, salary data, interview prep — and resume on any device." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://app.blackmount.ai/use-cases/job-search" />
        <meta property="og:image" content="https://app.blackmount.ai/og-image.png" />
        <meta property="og:site_name" content="Blackmount" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Never Lose Your Job Research — Save 30 Tabs | Blackmount" />
        <meta name="twitter:description" content="Save your entire job search session — listings, salary data, interview prep — and resume on any device." />
        <meta name="twitter:image" content="https://app.blackmount.ai/og-image.png" />
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Top Bar */}
        <AppBar
          position="static"
          elevation={0}
          sx={{ bgcolor: 'background.paper', borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          <Toolbar>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <Box
                component={Link}
                to="/"
                sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
              >
                <Box
                  component="img"
                  src="/blackmount-logo.jpg"
                  alt="Blackmount"
                  sx={{ height: 32, width: 'auto', borderRadius: 0.5 }}
                />
              </Box>
            </Box>
            <IconButton color="inherit" onClick={toggleDarkMode}>
              {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Hero */}
        <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 }, textAlign: 'center' }}>
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            component="h1"
            fontWeight={700}
            sx={{ mb: 3, color: 'text.primary' }}
          >
            Never Lose Your Job Research
          </Typography>

          <Typography
            variant={isMobile ? 'body1' : 'h6'}
            color="text.secondary"
            sx={{ mb: 6, maxWidth: 620, mx: 'auto', lineHeight: 1.6 }}
          >
            You have 30 tabs open — job listings, Glassdoor reviews, LinkedIn profiles, salary
            comparisons, interview prep. You close your laptop and they're gone.
          </Typography>

          {/* Screenshot placeholder */}
          <Box
            sx={{
              mx: 'auto',
              mb: 8,
              maxWidth: 600,
              height: 260,
              borderRadius: 3,
              border: `2px dashed ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: theme.palette.action.hover,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Screenshot coming soon
            </Typography>
          </Box>

          {/* How it works */}
          <Typography variant="h5" fontWeight={600} sx={{ mb: 4, color: 'text.primary' }}>
            How it works
          </Typography>

          <Stack spacing={4} sx={{ maxWidth: 520, mx: 'auto', textAlign: 'left', mb: 8 }}>
            {STEPS.map((step) => (
              <Box key={step.num} sx={{ display: 'flex', gap: 2 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    flexShrink: 0,
                  }}
                >
                  {step.num}
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>

          {/* CTA */}
          <Button
            variant="contained"
            size="large"
            startIcon={<ExtensionIcon />}
            onClick={() => window.open(CHROME_URL, '_blank', 'noopener,noreferrer')}
            sx={{
              py: 1.5,
              px: 4,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 3,
              textTransform: 'none',
            }}
          >
            Add to Chrome — Free
          </Button>
        </Container>
      </Box>
    </>
  );
};

export default JobSearch;
